import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import left from "../../../assets/icons/Arrow/left.svg"
import { HeaderIconL } from '../../../assets/components/HeaderGroup';
import { useFixedLayout } from '../../../hooks/useFixedLayout';
import { formatRelativeTime } from '../../../utils/formatTime';
import { postToApp, addAppMessageListener } from '../../../bridges/appBridge';
import download from "../../../assets/icons/System/downloade.svg"
import { useToast } from '../../../hooks/useToast';
import SectionTitle from '../../../assets/components/TitleGroup';


const buildUploadUrl = (path) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;

    const base =
        process.env.REACT_APP_UPLOAD_BASE_URL ||
        `${window.location.protocol}//${window.location.host}`;

    const cleaned = path.replace(/^\/+/, "");
    const withUploads = cleaned.startsWith("uploads/")
        ? cleaned
        : `uploads/${cleaned}`;

    return `${base}/${withUploads}`;
};

const AdsNoticeDetail = () => {
    const location = useLocation();
    const {
        notice,
        storeBusinessNumber,
        fromTab,
        source = "notice",
    } = location.state || {};
    const navigate = useNavigate();
    const { showToast } = useToast();

    const resolvedFromTab = fromTab;
    const { headerRef, headerSpace } = useFixedLayout();
    const sentRef = useRef(false);

    useEffect(() => {
        const unsub = addAppMessageListener((msg) => {
            if (msg.type !== "DOWNLOAD_FILE_RESULT") return;

            const { ok, path, filename, error } = msg.payload || {};

            if (ok) {
                // ✅ 성공 처리
                showToast("파일이 기기에 저장되었어요", {
                    placement: "bottom",
                    bottomOffset: "8px",
                });
                console.log("파일 다운로드 성공:", filename, path);
            } else {
                // ❌ 실패 처리
                showToast(`파일 다운로드에 실패했어요: ${error}`);
                console.error("파일 다운로드 실패:", error);
            }
        });

        return () => {
            // 컴포넌트 언마운트 시 리스너 제거
            unsub && unsub();
        };
    }, [showToast]);

    useEffect(() => {
        if (sentRef.current) return;
        if (!notice?.notice_no) return;

        sentRef.current = true;
        axios
            .post(
                `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/notice/view/${notice.notice_no}`
            )
            .catch(() => { });
    }, [notice?.notice_no]);

    if (!notice) {
        return <div>공지사항을 찾을 수 없습니다.</div>;
    }



    const handleBackToList = () => {
        // 1) 알림(AdsAlarmList)에서 온 경우 → 탭 유지해서 /ads/alarm 으로
        const initFilter = notice.notice_type === "이벤트" ? "event" : "normal";
        if (source === "alarm") {

            navigate("/ads/alarm", {
                state: {
                    storeBusinessNumber,
                    fromTab: resolvedFromTab,
                    alarmTab: initFilter,        // AdsAlarmList 초기 탭
                },
            });
            return;
        }

        // 2) /ads/notice 에서 온 경우 → 필터 유지해서 /ads/notice 로
        navigate("/ads/notice", {
            state: {
                storeBusinessNumber,
                fromTab: resolvedFromTab,
                noticeFilter: initFilter,  // AdsNotice에서 필터 유지
            },
        });
    };

    const fileDownloadUrl = notice.notice_no
        ? `${process.env.REACT_APP_UPLOAD_BASE_URL}/uploads/${notice.notice_file}`
        : "";

    const imageUrls = (notice.notice_images || []).map((p) => buildUploadUrl(p));
    const imageCount = imageUrls.length;
    const hasImages = imageCount > 0;
    const isSingleImage = imageCount === 1;

    const hasFile = !!notice.notice_file;
    const fileName =
        notice.notice_file_org ||
        (notice.notice_file ? notice.notice_file.split("/").pop() : "");


    const headerTitle = notice.notice_type === "일반" ? "공지사항" : "이벤트";

    const handleFileDownload = () => {
        if (window.ReactNativeWebView) {
            // 📱 앱 안일 때: RN에게 파일 저장 시키기
            postToApp({
                type: "DOWNLOAD_FILE",
                payload: {
                    url: fileDownloadUrl,
                    filename: fileName || "notice-file",
                },
            });
            return;
        }

        // 🌐 그냥 브라우저에서 열었을 때만 새 창으로 오픈
        if (fileDownloadUrl) {
            window.open(fileDownloadUrl, "_blank", "noopener,noreferrer");
        }
    };


    return (
        <div className="px-[16px]">
            {/* 고정 헤더 */}
            <div ref={headerRef} className="fixed top-0 left-0 right-0 z-10">
                <HeaderIconL
                    className=""
                    title={headerTitle}
                    icon={left}
                    onIconClick={handleBackToList}
                />
            </div>

            <div 

            className='border-b border-border-subtle'
            style={{ paddingTop: headerSpace }}>
                <SectionTitle
                    title={notice.notice_title}
                    subTitle={formatRelativeTime(notice.created_at)}
                    className={"pt-[24px] pb-[16px]"}
                />
            </div>

            {/* 첨부 이미지 / 본문 / 파일 영역 */}
            <section className="pt-[24px] space-y-[24px]">
                {/* 🔹 첨부 이미지 */}
                {hasImages && (
                    isSingleImage ? (
                        // 이미지 1장일 때: 가운데 큰 카드
                        <div className=" flex justify-center">
                            <div className="w-full max-w-[360px] aspect-[4/3] rounded-[12px] bg-surface-subtle border border-surface-subtle overflow-hidden">
                                <img
                                    src={imageUrls[0]}
                                    alt="첨부 이미지"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                    ) : (
                        // 이미지 여러 장일 때: 가로 스크롤 카드

                        <div className=" ">
                            <div className="flex gap-[10px] overflow-x-auto no-scrollbar">
                                {imageUrls.map((url, idx) => (
                                    <div
                                        key={idx}
                                        className="flex-none w-[220px] h-[220px] rounded-[12px] bg-surface-subtle border border-surface-subtle overflow-hidden"
                                    >
                                        <img
                                            src={url}
                                            alt={`첨부 이미지 ${idx + 1}`}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                    )
                )}

                {/* 본문 */}
                <p className="text-b2 text-text-subtle whitespace-pre-line">
                    {notice.notice_content}
                </p>

                {/* 🔹 첨부파일 */}
                {hasFile && (
                    <div className="">
                        <button
                            type="button"
                            onClick={handleFileDownload}
                            className="w-full flex items-center gap-[4px] px-[12px] py-[11px] rounded-[12px] bg-surface-subtle border border-border-default text-c2 text-text-default"
                        >
                            <img src={download} alt="다운로드"></img>
                            <span className="truncate">
                                {fileName || "첨부파일 다운로드"}
                            </span>
                        </button>
                    </div>
                )}
            </section>
        </div>
    );
};


export default AdsNoticeDetail;
